import React, { forwardRef, useState, useEffect } from "react";
import cx from "classnames";
import styled from 'styled-components';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import { TooltipTrigger } from '../Tooltip/Tooltip';
import IBuildrProps from '../../utilities/IBuildrProps';
import useLanguage from '../../utilities/useLanguage';
import { formatNumber } from "../../utilities/formatNumber";
import { getRate, IRate } from "../../services/BlackboxService";
import { MORTGAGE_TYPE } from "../Rate/RateTypes";
import { getTranslation } from "../Rate/translations";

interface IRateCard extends IBuildrProps {
    rateId: number
    headline: string;
    subhead: string;
    ctaText: string;
    ctaHyperLink: string;
    rateType: string;
    widgetType;
}

export const RatePercentage = styled(({rate, lang = 'en', className = ""}) => {
    let decimals = 2;

    if (rate.product.type === MORTGAGE_TYPE) {
        decimals = 3;
    }

    return (
        <div className={className}>
            {Boolean(rate.apy) ?
                <p className="d-flex apy">
                    <span>{`${formatNumber(rate.apy, decimals)}`}</span>
                    <span className="d-inline-flex flex-column">
                        <span className="symbol">%</span>
                        <span className="subtext">{getTranslation(lang).apy}<sup className="double-dagger">‡</sup></span>
                    </span>
                </p>
            :
                <p className="initial-rate">
                    <span className={cx("small-text")}>{getTranslation(lang).asLowAs}</span>
                    <span className="d-inline-flex">
                        <span>{`${formatNumber(rate.initialRate, decimals)}`}</span>
                        <span className="d-inline-flex flex-column">
                            <span className={cx({'symbol': !rate.disclosureInformation?.apr})}>%</span>
                            {!rate.disclosureInformation?.apr ? <span className="subtext">{getTranslation(lang).apr}<sup className="double-dagger">‡</sup></span> : null}
                        </span>
                    </span>
                </p>
            }
            {rate.disclosureInformation?.apr ?
                <p className="d-flex apr">
                    <span>({`${formatNumber(rate.disclosureInformation.apr, decimals)}`}</span>
                    <span className="d-inline-flex flex-column">
                        <span className="symbol">%</span>
                        <span className="subtext">{getTranslation(lang).apr}<sup className="double-dagger">‡</sup></span>
                    </span>
                    <span>)</span>
                </p>
            : null}
        </div>
    )
})`
.small-text {
    display: block;
    font-size: 0.667rem;
    font-weight: 400;
    text-transform: uppercase;
    min-height: 20px;
}

.symbol {
    font-size: 1.834rem;
}

.subtext {
    font-size: 0.667rem;
    margin-top: -5px;
}
.double-dagger {
    font-weight: 300;
}
`;

const initialRateState = {
    apy: 0,
    initialRate: null,

    disclosureInformation: {
        apr: null
    },
    product: {
        type: 0,
        disclosure: {
            disclosureData: null,
            spanishDisclosureData: null
        }
    }
};
export default styled(forwardRef((props: IRateCard, ref: any) => {
    const { lang } = useLanguage();

    const { cmsId, editorClassObj, className, rateId, headline, subhead, ctaText, ctaHyperLink, rateType } = props;
    const [rate, setRate] = useState(initialRateState);

    useEffect(() => {
        loadRate();
    }, [rateId, rateType])

    async function loadRate() {
        try {
            const rateData: IRate = await getRate(rateId, rateType);
            setRate(rateData.rate || initialRateState);
        } catch (error) {
            console.error(error);
            setRate(initialRateState);
        }
    }

    return (
        <div ref={ref} data-id={cmsId} className={cx(className, editorClassObj, 'd-flex flex-column position-relative')}>
            <div className="header">
                <p className="headline">{headline}</p>
                {subhead ?
                    <p className="subhead">{subhead}</p>
                : null}
            </div>
            <div className={cx('content', {'apy-rate': Boolean(rate.apy)})}>
                {!Boolean(rate.apy) && !Boolean(rate.initialRate) ?
                    <p>N/A</p>
                :
                    <RatePercentage rate={rate} lang={lang}/>
                }

            </div>
            <div className="footer d-flex justify-content-between align-items-center">
                {(ctaText && ctaHyperLink) ?
                    <a className="cta stretched-link" href={ctaHyperLink}>{ctaText}</a>
                : null
                }
                {rate.product.disclosure?.disclosureData ?
                    <TooltipTrigger
                        className="d-md-block d-xl-block"
                        html={(lang === 'es' && rate.product.disclosure?.spanishDisclosureData) ? rate.product.disclosure?.spanishDisclosureData : rate.product.disclosure?.disclosureData}
                        place="right"
                    >
                        <AiOutlineInfoCircle />
                    </TooltipTrigger>
                : null}

            </div>


        </div>
    );
}))`

padding: 1rem 1rem 1.5rem;
border: 1px solid #CDC7C4;
border-radius: 0.2rem;
width: calc(100% / 4 - (15px * 3 / 4));

p {
    margin: 0;
}

@media(max-width: 1200px){
    width: calc(100% / 2 - (15px * 1 / 2));
}

@media(max-width: 992px) {
    width: 100%;
    flex: 1 0 90%;
}

.header {
    border-bottom: 2px solid #E0DCDB;
    min-height: 70px;
    .headline {
        font-size: 1.111rem;
        font-weight: 700;
    }
    .subhead {
        font-size: 1.111rem;
        font-weight: 500;
    }
}

.content {
    flex-grow: 1;
    padding: .8rem 0 3rem;
    p {
        font-family: "jubilat-variable",sans-serif;
        font-variation-settings: 'wght' 310;
        font-size: 2.804rem;
        line-height: 1;
        padding-bottom: 7px;
    }
    &.apy-rate {
        padding-top: calc(0.8rem + 20px )
    }
}

.footer {
    .cta {
        font-weight: 500;
        font-size: 0.778rem;
        border-bottom: 1px solid black;
        text-decoration: none;
        color: black;
    }

    .macu-tooltip-trigger {
        cursor: pointer;
        margin-left: auto;
        z-index: 100;
    }
}

&:hover {
    cursor: pointer;
    background: var(--redRock);
    border: 1px solid var(--redRock);
    color: white;
    .header {
        border-color: white;
    }
    .footer {
        .cta {
            color: white;
            border-color: white;
        }

        .macu-tooltip-trigger {
            color: white;
        }
    }
}

`;
